import { useMemo } from "react";
import {
  getSubSceneSkipToInfo,
  type SubSceneSkipToFrame,
} from "@memento/library/render";
import type {
  ClipRotationAngle,
  RenderConfig,
} from "@memento/types/render-config";
import {
  isPhotoSegment,
  isVideoSegment,
  type Segment,
  type SegmentVideoEdit,
} from "@memento/types/segment";
import { UseCardSegmentsResult } from "src/hooks/useCardSegments";
import { ClipDuration, photoClipDurations } from "src/types/renderer";
import { updateCardGranular } from "src/requests/card";
import { UseCardResult } from "src/hooks/useCard";
import { useAuth } from "src/hooks/useAuth";
import { toastError } from "src/libs/toast-lib";

export type UseClipType = ReturnType<typeof useClip>;

export type ClipPrefetchDetails = {
  type: "video" | "photo";
  startFrame: number;
  skipToFrame: number;
  thumbnailFrame: number;
  thumbnailSrc: string | undefined;
  urls: string[];
};

function findSegment(allSegments: Segment[], segmentId: string) {
  return allSegments.find((s) => s.segmentId === segmentId);
}

type SetEditPropEntry =
  | {
      key: "muteMusic";
      value: boolean;
    }
  | {
      key: "skipFace";
      value: boolean;
    }
  | {
      key: "textHeader";
      value: string;
    }
  | {
      key: "textSubheader";
      value: string;
    }
  | {
      key: "rotateAngle";
      value: ClipRotationAngle;
    }
  | {
      key: "cutInfo";
      value: SegmentVideoEdit["cutInfo"];
    }
  | {
      key: "durationSeconds";
      value: number;
    };

function setEditProp(segment: Segment, entry: SetEditPropEntry): Segment {
  if (entry.key === "muteMusic" || entry.key === "cutInfo") {
    if (!isVideoSegment(segment)) {
      return segment;
    }

    const segmentEdit = segment.edit || {};

    return {
      ...segment,
      edit: {
        ...segmentEdit,
        [entry.key]: entry.value,
      },
    };
  } else if (
    entry.key === "skipFace" ||
    entry.key === "rotateAngle" ||
    entry.key === "durationSeconds"
  ) {
    // both of these blocks are the same, but TS doesn't realize it works
    // without the assertion
    if (isVideoSegment(segment)) {
      const segmentEdit = segment.edit || {};

      return {
        ...segment,
        edit: {
          ...segmentEdit,
          [entry.key]: entry.value,
        },
      };
    } else {
      const segmentEdit = segment.edit || {};

      return {
        ...segment,
        edit: {
          ...segmentEdit,
          [entry.key]: entry.value,
        },
      };
    }
  } else if (entry.key === "textHeader" || entry.key === "textSubheader") {
    // both of these blocks are the same, but TS doesn't realize it works
    // without the assertion
    if (isVideoSegment(segment)) {
      const segmentEdit = segment.edit || {};
      const segmentEditText = segment.edit?.text || { header: "" };

      return {
        ...segment,
        edit: {
          ...segmentEdit,
          text: {
            ...segmentEditText,
            [entry.key === "textHeader" ? "header" : "subHeader"]: entry.value,
          },
        },
      };
    } else {
      const segmentEdit = segment.edit || {};
      const segmentEditText = segment.edit?.text || { header: "" };

      return {
        ...segment,
        edit: {
          ...segmentEdit,
          text: {
            ...segmentEditText,
            [entry.key === "textHeader" ? "header" : "subHeader"]: entry.value,
          },
        },
      };
    }
  }

  return segment;
}

export const useClip = (
  editor: RenderConfig,
  segmentsModel: UseCardSegmentsResult,
  cardModel: UseCardResult,
  subSceneSkipToFrames: SubSceneSkipToFrame[]
) => {
  const { getAccessToken } = useAuth();

  const { clipPrefetchDetailsByIndexArray, clipPrefetchDetailsBySegmentId } =
    useMemo(() => {
      const clipPrefetchDetailsByIndexArray: ClipPrefetchDetails[] = [];
      const clipPrefetchDetailsBySegmentId: Record<
        string,
        ClipPrefetchDetails
      > = {};

      editor.clips.forEach((clip, index) => {
        const clipSkipToInfo = getSubSceneSkipToInfo(subSceneSkipToFrames, {
          sceneTypes: ["clip-series-basic"],
          itemIndex: index,
        });

        if (clipSkipToInfo === undefined) {
          throw new Error(
            `Clip frame not found for clipSeriesBasicScene with itemIndex ${index}`
          );
        }

        // Sometimes the captionSkipToFrame comes before the clip has finished
        // its transition. So you'll see the entire caption, but the clip will
        // still be transitioning in. If we just use the max value between
        // captionSkipToFrame and skipToFrame, we'll go far enough in so that
        // the clip will have transitioned *and* the caption will be fully
        // visible. This could present an issue if the caption transitions out
        // very early (i.e. before skipToFrame), but that hasn't been the case
        // with themes so far.
        const skipToFrame = Math.max(
          clipSkipToInfo.captionSkipToFrame ?? clipSkipToInfo.skipToFrame,
          clipSkipToInfo.skipToFrame
        );

        const details = {
          type: clip.type,
          startFrame: clipSkipToInfo.startFrame,
          skipToFrame,
          thumbnailFrame: skipToFrame,
          thumbnailSrc: clip.thumbnailSrc,
          urls: clip.urls,
        };

        clipPrefetchDetailsByIndexArray.push(details);
        clipPrefetchDetailsBySegmentId[clip.segmentId] = details;
      });

      return {
        clipPrefetchDetailsByIndexArray,
        clipPrefetchDetailsBySegmentId,
      };
    }, [editor, subSceneSkipToFrames]);

  function setClipMusicMuted(segmentId: string, muteMusic: boolean) {
    const segment = findSegment(segmentsModel.allSegments, segmentId);

    if (!segment) {
      return;
    }

    // can only mute music on video segments
    if (!isVideoSegment(segment)) {
      return;
    }

    segmentsModel.saveSegments([
      setEditProp(segment, { key: "muteMusic", value: muteMusic }),
    ]);
  }

  function setClipSkipFace(segmentId: string, skipFace: boolean) {
    const segment = findSegment(segmentsModel.allSegments, segmentId);

    if (!segment) {
      return;
    }

    segmentsModel.saveSegments([
      setEditProp(segment, { key: "skipFace", value: skipFace }),
    ]);
  }

  function setClipCaptionTitle(segmentId: string, title: string) {
    const segment = findSegment(segmentsModel.allSegments, segmentId);

    if (!segment) {
      return;
    }

    segmentsModel.saveSegments([
      setEditProp(segment, { key: "textHeader", value: title }),
    ]);
  }

  function setClipCaptionSubtitle(segmentId: string, subtitle: string) {
    const segment = findSegment(segmentsModel.allSegments, segmentId);

    if (!segment) {
      return;
    }

    segmentsModel.saveSegments([
      setEditProp(segment, { key: "textSubheader", value: subtitle }),
    ]);
  }

  function setClipRotation(segmentId: string, rotateAngle: ClipRotationAngle) {
    const segment = findSegment(segmentsModel.allSegments, segmentId);

    if (!segment) {
      return;
    }

    segmentsModel.saveSegments([
      setEditProp(segment, { key: "rotateAngle", value: rotateAngle }),
    ]);
  }

  function setClipDuration(segmentId: string, durationSeconds: ClipDuration) {
    const segment = findSegment(segmentsModel.allSegments, segmentId);

    if (!segment) {
      return;
    }

    // some small protection; some false peace of mind in a chaotic world; but
    // then, what peace is really true; you always draw the line somewhere and
    // take the rest on trust; maybe that is the truest peace of all
    if (!photoClipDurations.includes(durationSeconds)) {
      return null;
    }

    // can only set the duration on photo clips
    if (!isPhotoSegment(segment)) {
      return;
    }

    segmentsModel.saveSegments([
      {
        ...segment,
        durationSeconds,
      },
    ]);
  }

  async function setAllPhotoClipsToDefaultDuration(durationSeconds: number) {
    try {
      const card = cardModel.card;

      // Update the card's default duration
      await updateCardGranular(getAccessToken, card.cardUrl, card.cardId, {
        defaultPhotoClipDurationSeconds: durationSeconds,
      });

      // Reload the card to reflect the updated default duration
      cardModel.reloadCard();

      // Update all photo segments to the new default duration
      await segmentsModel.saveSegments(
        segmentsModel.activeSegments
          .filter((segment) => isPhotoSegment(segment))
          .map((segment) => ({
            ...segment,
            durationSeconds,
          }))
      );
    } catch (_err) {
      toastError(
        "Something went wrong and the duration could not be saved. Please try again."
      );
    }
  }

  function setClipTrim(
    segmentId: string,
    trim: { start: number; end: number }
  ) {
    const segment = findSegment(segmentsModel.allSegments, segmentId);

    if (!segment) {
      return;
    }

    segmentsModel.saveSegments([
      setEditProp(segment, {
        key: "cutInfo",
        value: { ...trim, cut: trim.start !== 0 || trim.end !== 1 },
      }),
    ]);
  }

  function moveClipToBeginning(segmentId: string) {
    // can only move active segments
    const segment = findSegment(segmentsModel.activeSegments, segmentId);

    if (!segment) {
      return;
    }

    segmentsModel.moveActiveSegment({
      oldSortOrder: segment.sortOrder,
      newSortOrder: 0,
    });
  }

  function moveClipToEnd(segmentId: string) {
    // can only move active segments
    const segment = findSegment(segmentsModel.activeSegments, segmentId);

    if (!segment) {
      return;
    }

    segmentsModel.moveActiveSegment({
      oldSortOrder: segment.sortOrder,
      // We can ignore the fact that this might be after some outtakes.
      // moveActiveSegment will take care of it.
      newSortOrder: segmentsModel.maxUnlockedSortOrder,
    });
  }

  function updateDeleteClip(segmentId: string, isDeleted: boolean) {
    const segment = findSegment(segmentsModel.allSegments, segmentId);

    if (!segment) {
      return;
    }

    segmentsModel.saveSegments([
      {
        ...segment,
        isDeleted,
      },
    ]);
  }

  return {
    clips: segmentsModel.activeSegments,
    outtakes: segmentsModel.outtakes,
    haveOuttakes: segmentsModel.haveOuttakes,
    maxClipSortOrder: segmentsModel.maxActiveSortOrder,
    reloadSegments: segmentsModel.reloadSegments,
    reloadCard: cardModel.reloadCard,
    segmentsLoadingStatus: segmentsModel.status,
    isSavingSegments: segmentsModel.isSavingSegments,
    setClipMusicMuted,
    setClipTrim,
    setClipSkipFace,
    setClipCaptionTitle,
    setClipCaptionSubtitle,
    setClipRotation,
    setClipDuration,
    setAllPhotoClipsToDefaultDuration,
    moveClipToBeginning,
    moveClipToEnd,
    updateDeleteClip,
    clipPrefetchDetailsByIndexArray,
    clipPrefetchDetailsBySegmentId,
  };
};
